import { usePersistedFilters } from "components/source/hoc/with-persisted-filters";
import { useAppDispatch, useAppSelector } from "../../../util/store-types";
import { changeFilters, setMembershipLens } from "actions/products-actions";
import { isStorefrontNext } from "helpers/environment-helpers";
import { getRubySubscriptionAvailabilityFilter, isViewingAvailableOnly } from "helpers/grid-migration-helpers";
import AvailabilityFilterHelpers from "helpers/availability-filter-helpers";
import MembershipHelpers from "helpers/membership-helpers";
import { inventoryEligibilities, membershipLens, AVAILABILITY_SCOPES } from "rtr-constants";
import type { WorkingFilters } from "components/types/working-filters";
import type { WithRequiredFields } from "components/types/type-utils";
import { updateAvailabilityScope } from "slices/products-slice";

/**
 * Handle client-side toggle between subscription and reserve availabilities on a grid.
 * @param onSubmitFilters - code called after relevant filters are updated (i.e. to reload products).
 */
export const useAvailabilityFilters = (onSubmitFilters: () => void) => {
  const reserveFilterKeys: (keyof WorkingFilters)[] = ["date", "duration"];
  const membershipFilterKeys: (keyof WorkingFilters)[] = [
    "availableOnly",
    "programEligibility",
    AvailabilityFilterHelpers.unlimitedMinAvailabilityKey,
    AvailabilityFilterHelpers.rtrUpdateMinAvailabilityFilterKey,
  ];

  const workingFilters = useAppSelector(state => state.workingFilters);
  const isClassicLens = useAppSelector(state => state.userData?.isClassicLens);
  const currentAvailableOnlyParam = useAppSelector(state =>
    isViewingAvailableOnly(state.workingFilters, state.membershipState)
  );
  const isBasicCloset = useAppSelector(
    state =>
      //Prospects should always see full closet inventory, including users who formerly had basic closet but canceled
      //These users will still have the rtr_update inventory eligibility on their membership state
      MembershipHelpers.isActiveMembership(state.membershipState) &&
      MembershipHelpers.hasInventoryEligibility(state.membershipState, inventoryEligibilities.rtr_update)
  );
  const programEligibility = isBasicCloset ? "BASIC_CLOSET" : "FULL_CLOSET";
  const dispatch = useAppDispatch();
  const { clearPreferences, writePreferences } = usePersistedFilters();

  /**
   * Either apply the default availability view for membership inventory based on user type (if currently viewing Reserve)
   * or toggle the value of the existing filter (if already viewing membership)
   */
  const setOrToggleMembershipAvailability = () => {
    const newLens = isBasicCloset ? membershipLens.RTRUpdate : membershipLens.unlimited;

    //If the user is switching back to membership from reserve, apply the default availability filter
    //Otherwise, toggle the existing filter
    const availableOnlyParam = isClassicLens
      ? AvailabilityFilterHelpers.getDefaultAvailableOnlyValue()
      : !currentAvailableOnlyParam;

    if (isClassicLens) {
      dispatch(setMembershipLens(newLens, false));
    }

    dispatch(updateAvailabilityScope(AVAILABILITY_SCOPES.MEMBERSHIP));

    const updatedWorkingFilters: WorkingFilters = isStorefrontNext()
      ? { ...workingFilters, availableOnly: availableOnlyParam, programEligibility }
      : {
          ...workingFilters,
          ...getRubySubscriptionAvailabilityFilter(availableOnlyParam, isBasicCloset),
        };

    //Remove any Reserve filters, both from WorkingFilters and from any persisted filters
    reserveFilterKeys.forEach(k => delete updatedWorkingFilters[k]);
    clearPreferences(reserveFilterKeys);

    dispatch(changeFilters(updatedWorkingFilters));
    writePreferences(updatedWorkingFilters);

    onSubmitFilters();
  };

  const setReserveAvailability = (date: string | null, duration: WorkingFilters["duration"] = "4") => {
    //It should no longer be possible to view a Reserve grid without dates set
    //If none are present (because dates have been cleared), toggle back to Membership grid
    if (date === null) {
      return setOrToggleMembershipAvailability();
    }

    if (!isClassicLens) {
      dispatch(setMembershipLens(membershipLens.classic, false));
    }
    dispatch(updateAvailabilityScope(AVAILABILITY_SCOPES.RESERVE));

    const updatedWorkingFilters: WithRequiredFields<WorkingFilters, "date" | "duration"> = {
      ...workingFilters,
      date,
      duration,
    };

    //Remove any membership filters, both from WorkingFilters and from any persisted filters
    membershipFilterKeys.forEach(k => delete updatedWorkingFilters[k]);
    clearPreferences(membershipFilterKeys);

    dispatch(changeFilters(updatedWorkingFilters));
    writePreferences(updatedWorkingFilters);

    onSubmitFilters();
  };

  return { setOrToggleMembershipAvailability, setReserveAvailability };
};
